import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AudioRecorderService } from '@nimbus-library/features/shared/services/media/audio-recorder.service';

export enum VoiceRecordingState {
  Idle = 'idle',
  Recording = 'recording',
  Preview = 'preview',
}

@Injectable({ providedIn: 'root' })
export class ChatVoiceRecorderService implements OnDestroy {
  public readonly state$ = new BehaviorSubject<VoiceRecordingState>(VoiceRecordingState.Idle);
  public readonly seconds$ = new BehaviorSubject<number>(0);
  public readonly blob$ = new BehaviorSubject<Blob | null>(null);
  public readonly error$ = new BehaviorSubject<boolean>(false);

  private chunks: ArrayBuffer[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;

  public constructor(
    private readonly ngZone: NgZone,
    private readonly audioRecorder: AudioRecorderService,
  ) {}

  public async start(): Promise<void> {
    try {
      this.chunks = [];
      this.seconds$.next(0);

      await this.audioRecorder.start(
        (chunk) => this.chunks.push(chunk),
        () =>
          this.ngZone.run(() => {
            this.blob$.next(new Blob(this.chunks, { type: 'audio/ogg; codecs=opus' }));
            this.state$.next(VoiceRecordingState.Preview);
          }),
      );
      this.error$.next(false);
      this.state$.next(VoiceRecordingState.Recording);
      this.timer = setInterval(() => this.seconds$.next(this.seconds$.value + 1), 1000);
    } catch {
      this.error$.next(true);
    }
  }

  public stop(): void {
    this.clearTimer();
    this.audioRecorder.stop();
  }

  public discard(): void {
    this.clearTimer();
    this.audioRecorder.stop();
    this.blob$.next(null);
    this.state$.next(VoiceRecordingState.Idle);
    this.seconds$.next(0);
    this.error$.next(false);
  }

  public ngOnDestroy(): void {
    this.discard();
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
